import React, { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { ArrowLeft, ArrowRight, Calendar, BarChart3, Settings, Edit2, Trash2, Save, X } from 'lucide-react'
import { Resistance, api, ResistanceAnalytics } from '../lib/api'

export function ResistanceDetail() {
  const { resistanceId } = useParams<{ resistanceId: string }>()
  const navigate = useNavigate()
  
  const [resistance, setResistance] = useState<Resistance | null>(null)
  const [analytics, setAnalytics] = useState<ResistanceAnalytics | null>(null)
  const [loading, setLoading] = useState(true)
  const [selectedDate, setSelectedDate] = useState<string | null>(null)
  const [viewMode, setViewMode] = useState<'month' | 'week'>('month')
  const [currentDate, setCurrentDate] = useState(new Date())
  const [isEditing, setIsEditing] = useState(false)
  const [editValues, setEditValues] = useState({
    daily_limit: 0,
    session_duration_minutes: 0
  })

  useEffect(() => {
    loadResistance()
    loadAnalytics()
  }, [resistanceId])

  const loadResistance = async () => {
    if (!resistanceId) return
    
    try {
      const resistances = await api.getResistances()
      const foundResistance = resistances.find(r => r.id === resistanceId)
      
      if (foundResistance) {
        setResistance(foundResistance)
        setEditValues({
          daily_limit: foundResistance.daily_limit,
          session_duration_minutes: foundResistance.session_duration_minutes
        })
      } else {
        navigate('/dashboard')
      }
    } catch (err) {
      console.error('Failed to load resistance:', err)
      navigate('/dashboard')
    } finally {
      setLoading(false)
    }
  }

  const loadAnalytics = async () => {
    if (!resistanceId) return
    
    try {
      const data = await api.getResistanceAnalytics(resistanceId, 30)
      setAnalytics(data)
    } catch (err) {
      console.error('Failed to load analytics:', err)
    }
  }

  const handleUpdate = async (updates: Partial<Resistance>) => {
    if (!resistance) return
    
    try {
      const updatedResistance = await api.updateResistance(resistance.id, updates)
      setResistance(updatedResistance)
    } catch (err) {
      console.error('Failed to update resistance:', err)
    }
  }

  const handleDelete = async () => {
    if (!resistance) return
    
    if (confirm(`Are you sure you want to delete the resistance for ${resistance.domain}?`)) {
      try {
        await api.deleteResistance(resistance.id)
        navigate('/dashboard')
      } catch (err) {
        console.error('Failed to delete resistance:', err)
      }
    }
  }

  const handleSave = () => {
    handleUpdate(editValues)
    setIsEditing(false)
  }

  const handleCancel = () => {
    setEditValues({
      daily_limit: resistance?.daily_limit || 0,
      session_duration_minutes: resistance?.session_duration_minutes || 0
    })
    setIsEditing(false)
  }

  const formatDateToLocal = (date: Date): string => {
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')
    return `${year}-${month}-${day}`
  }

  const getSessionIntensity = (date: string): number => {
    if (!analytics || !analytics.sessionsByDate[date]) return 0
    const sessions = analytics.sessionsByDate[date]
    const limit = resistance?.daily_limit || 10
    return sessions / limit
  }

  const getIntensityColor = (intensity: number): string => {
    if (intensity === 0) return 'bg-gray-100'
    if (intensity <= 0.5) return 'bg-green-200'
    if (intensity < 1.0) return 'bg-yellow-200'
    return 'bg-red-200'
  }

  const renderCalendar = () => {
    if (viewMode === 'month') {
      return renderMonthView()
    } else {
      return renderWeekView()
    }
  }

  const renderMonthView = () => {
    const today = new Date()
    const year = currentDate.getFullYear()
    const month = currentDate.getMonth()
    const firstDayOfMonth = new Date(year, month, 1)
    const lastDayOfMonth = new Date(year, month + 1, 0)
    const firstDayOfWeek = firstDayOfMonth.getDay()
    const daysInMonth = lastDayOfMonth.getDate()

    const days = []
    
    // Add empty cells for days before the first day of the month
    for (let i = 0; i < firstDayOfWeek; i++) {
      days.push(<div key={`empty-${i}`} className="h-20"></div>)
    }

    // Add cells for each day of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(year, month, day)
      const dateStr = formatDateToLocal(date)
      const intensity = getSessionIntensity(dateStr)
      const sessions = analytics?.sessionsByDate[dateStr] || 0
      const isToday = date.toDateString() === today.toDateString()

      days.push(
        <div
          key={day}
          onClick={() => setSelectedDate(dateStr)}
          className={`h-20 p-2 border border-gray-200 cursor-pointer hover:bg-gray-50 ${
            getIntensityColor(intensity)
          } ${isToday ? 'ring-2 ring-blue-500' : ''}`}
        >
          <div className="text-sm font-medium">{day}</div>
          {sessions > 0 && (
            <div className="text-xs text-gray-600 mt-1">
              {sessions} session{sessions !== 1 ? 's' : ''}
            </div>
          )}
        </div>
      )
    }

    const monthName = firstDayOfMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })

    return (
      <div className="space-y-4">
        {/* Month Navigation Header */}
        <div className="flex items-center justify-between mb-4">
          <button
            onClick={() => setCurrentDate(new Date(year, month - 1, 1))}
            className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <h2 className="text-lg font-semibold">{monthName}</h2>
          <button
            onClick={() => setCurrentDate(new Date(year, month + 1, 1))}
            className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
          >
            <ArrowRight className="w-5 h-5" />
          </button>
        </div>

        <div className="grid grid-cols-7 gap-px bg-gray-200">
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
            <div key={day} className="bg-gray-50 p-2 text-center text-sm font-medium">
              {day}
            </div>
          ))}
          {days}
        </div>
        <div className="flex items-center justify-center space-x-4 text-sm">
          <div className="flex items-center space-x-2">
            <div className="w-4 h-4 bg-gray-100 rounded"></div>
            <span>No Activity</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-4 h-4 bg-green-200 rounded"></div>
            <span>Less Activity</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-4 h-4 bg-yellow-200 rounded"></div>
            <span>Moderate Activity</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-4 h-4 bg-red-200 rounded"></div>
            <span>Heavy Activity</span>
          </div>
        </div>
      </div>
    )
  }

  const renderWeekView = () => {
    const today = new Date()
    const startOfWeek = new Date(currentDate)
    startOfWeek.setDate(currentDate.getDate() - currentDate.getDay())
    
    const days = []

    for (let i = 0; i < 7; i++) {
      const date = new Date(startOfWeek)
      date.setDate(startOfWeek.getDate() + i)
      const dateStr = formatDateToLocal(date)
      const intensity = getSessionIntensity(dateStr)
      const sessions = analytics?.sessionsByDate[dateStr] || 0
      const isToday = date.toDateString() === today.toDateString()

      days.push(
        <div
          key={i}
          onClick={() => setSelectedDate(dateStr)}
          className={`p-4 border border-gray-200 cursor-pointer hover:bg-gray-50 ${
            getIntensityColor(intensity)
          } rounded-lg ${isToday ? 'ring-2 ring-blue-500' : ''}`}
        >
          <div className="text-lg font-bold">{date.getDate()}</div>
          <div className="text-sm text-gray-600">
            {date.toLocaleDateString('en-US', { weekday: 'short' })}
          </div>
          {sessions > 0 && (
            <div className="text-sm mt-2">
              {sessions} session{sessions !== 1 ? 's' : ''}
            </div>
          )}
        </div>
      )
    }

    const weekStartMonth = startOfWeek.toLocaleDateString('en-US', { month: 'short' })
    const weekEndMonth = new Date(startOfWeek.getTime() + 6 * 24 * 60 * 60 * 1000)
      .toLocaleDateString('en-US', { month: 'short' })

    const weekLabel = weekStartMonth === weekEndMonth 
      ? `${weekStartMonth} ${startOfWeek.getDate()}-${new Date(startOfWeek.getTime() + 6 * 24 * 60 * 60 * 1000).getDate()}, ${startOfWeek.getFullYear()}`
      : `${weekStartMonth} ${startOfWeek.getDate()} - ${weekEndMonth} ${new Date(startOfWeek.getTime() + 6 * 24 * 60 * 60 * 1000).getDate()}, ${startOfWeek.getFullYear()}`

    return (
      <div className="space-y-4">
        {/* Week Navigation Header */}
        <div className="flex items-center justify-between mb-4">
          <button
            onClick={() => setCurrentDate(new Date(currentDate.getTime() - 7 * 24 * 60 * 60 * 1000))}
            className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <h2 className="text-lg font-semibold">{weekLabel}</h2>
          <button
            onClick={() => setCurrentDate(new Date(currentDate.getTime() + 7 * 24 * 60 * 60 * 1000))}
            className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
          >
            <ArrowRight className="w-5 h-5" />
          </button>
        </div>

        <div className="grid grid-cols-7 gap-4">
          {days}
        </div>

        <div className="flex items-center justify-center space-x-4 text-sm mt-4">
          <div className="flex items-center space-x-2">
            <div className="w-4 h-4 bg-gray-100 rounded"></div>
            <span>No Activity</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-4 h-4 bg-green-200 rounded"></div>
            <span>Less Activity</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-4 h-4 bg-yellow-200 rounded"></div>
            <span>Moderate Activity</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-4 h-4 bg-red-200 rounded"></div>
            <span>Heavy Activity</span>
          </div>
        </div>
      </div>
    )
  }


  const getThisMonthSessions = () => {
    if (!analytics) return 0
    const today = new Date()
    const year = today.getFullYear()
    const month = today.getMonth()
    let totalSessions = 0

    const daysInMonth = new Date(year, month + 1, 0).getDate()
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(year, month, day)
      const dateStr = formatDateToLocal(date)
      totalSessions += analytics.sessionsByDate[dateStr] || 0
    }

    return totalSessions
  }

  const getCurrentViewSessions = () => {
    if (!analytics) return { total: 0, average: 0, days: 0 }

    const today = new Date()
    today.setHours(0, 0, 0, 0)

    if (viewMode === 'month') {
      const year = currentDate.getFullYear()
      const month = currentDate.getMonth()
      let totalSessions = 0
      const daysInMonth = new Date(year, month + 1, 0).getDate()
      const lastDayOfMonth = new Date(year, month + 1, 0)
      lastDayOfMonth.setHours(0, 0, 0, 0)

      // Calculate elapsed days: from start of month to today (or end of month if viewing past month)
      const endDate = today < lastDayOfMonth ? today : lastDayOfMonth
      const startDate = new Date(year, month, 1)
      const elapsedDays = Math.floor((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24)) + 1

      for (let day = 1; day <= daysInMonth; day++) {
        const date = new Date(year, month, day)
        const dateStr = formatDateToLocal(date)
        totalSessions += analytics.sessionsByDate[dateStr] || 0
      }

      return {
        total: totalSessions,
        average: elapsedDays > 0 ? totalSessions / elapsedDays : 0,
        days: elapsedDays
      }
    } else {
      // Week view
      const startOfWeek = new Date(currentDate)
      startOfWeek.setDate(currentDate.getDate() - currentDate.getDay())
      startOfWeek.setHours(0, 0, 0, 0)

      const endOfWeek = new Date(startOfWeek)
      endOfWeek.setDate(startOfWeek.getDate() + 6)
      endOfWeek.setHours(0, 0, 0, 0)

      // Calculate elapsed days: from start of week to today (or end of week if viewing past week)
      const endDate = today < endOfWeek ? today : endOfWeek
      const elapsedDays = Math.floor((endDate.getTime() - startOfWeek.getTime()) / (1000 * 60 * 60 * 24)) + 1

      let totalSessions = 0

      for (let i = 0; i < 7; i++) {
        const date = new Date(startOfWeek)
        date.setDate(startOfWeek.getDate() + i)
        const dateStr = formatDateToLocal(date)
        totalSessions += analytics.sessionsByDate[dateStr] || 0
      }

      return {
        total: totalSessions,
        average: elapsedDays > 0 ? totalSessions / elapsedDays : 0,
        days: elapsedDays
      }
    }
  }

  const generateAIInsights = () => {
    if (!analytics || !resistance) {
      return <p className="italic text-gray-500">Loading insights...</p>
    }

    const insights = []
    const dailyLimit = resistance.daily_limit
    const totalSessions = analytics.totalSessions

    // Analyze usage patterns
    if (totalSessions === 0) {
      insights.push(
        <p key="no-usage" className="p-3 bg-green-50 border-l-4 border-green-400 text-green-700">
          <strong>Great self-control!</strong> You haven't used {resistance.domain} in the tracked period. Keep it up!
        </p>
      )
    } else {
      // Check if over daily limit frequently
      const daysOverLimit = Object.entries(analytics.sessionsByDate)
        .filter(([_, sessions]) => sessions > dailyLimit).length

      if (daysOverLimit > 5) {
        insights.push(
          <p key="over-limit" className="p-3 bg-red-50 border-l-4 border-red-400 text-red-700">
            <strong>Pattern Alert:</strong> You've exceeded your daily limit on {daysOverLimit} days. Consider increasing your limit or setting stricter boundaries.
          </p>
        )
      }

      // Positive reinforcement for good behavior
      if (totalSessions < dailyLimit * 7 && daysOverLimit <= 1) {
        insights.push(
          <p key="good-control" className="p-3 bg-blue-50 border-l-4 border-blue-400 text-blue-700">
            <strong>Doing Well:</strong> You're maintaining good control over your {resistance.domain} usage. Your current pattern shows healthy self-regulation.
          </p>
        )
      }

      // Recent activity insights
      const recentSessions = analytics.recentSessions.slice(0, 3)
      if (recentSessions.length > 0) {
        const lastSession = recentSessions[0]
        const daysSinceLastSession = Math.floor((Date.now() - new Date(lastSession.startedAt).getTime()) / (1000 * 60 * 60 * 24))

        if (daysSinceLastSession > 3) {
          insights.push(
            <p key="break-streak" className="p-3 bg-green-50 border-l-4 border-green-400 text-green-700">
              <strong>Break Streak:</strong> It's been {daysSinceLastSession} days since your last session. Great job maintaining distance!
            </p>
          )
        }
      }
    }

    if (insights.length === 0) {
      insights.push(
        <p key="neutral" className="italic text-gray-500">
          Your usage patterns look normal. Continue monitoring your habits for personalized insights.
        </p>
      )
    }

    return insights
  }

  const renderDayDetailModal = () => {
    if (!selectedDate || !analytics) return null

    const sessionsForDay = analytics.recentSessions.filter(session => {
      const sessionDate = formatDateToLocal(new Date(session.startedAt))
      return sessionDate === selectedDate
    })
    const totalSessionsForDay = analytics.sessionsByDate[selectedDate] || 0
    const totalDurationForDay = analytics.durationByDate[selectedDate] || 0

    // Check if the selected date is in the future
    const selectedDateTime = new Date(selectedDate + 'T00:00:00')
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    const isFutureDate = selectedDateTime > today

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4 max-h-[80vh] overflow-y-auto">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-xl font-semibold">
              {new Date(selectedDate + 'T00:00:00').toLocaleDateString('en-US', { 
                weekday: 'long', 
                year: 'numeric', 
                month: 'long', 
                day: 'numeric' 
              })}
            </h3>
            <button
              onClick={() => setSelectedDate(null)}
              className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Day Summary */}
          <div className="grid grid-cols-1 gap-4 mb-6">
            <div className="text-center p-4 bg-blue-50 rounded-lg">
              <div className="text-2xl font-bold text-blue-600">{totalSessionsForDay}</div>
              <div className="text-sm text-blue-600">Sessions</div>
            </div>
          </div>

          {/* Daily Limit Status - Only show for past/present dates */}
          {!isFutureDate && (
            <div className="mb-6">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium">Daily Limit Progress</span>
                <span className={`text-sm font-medium ${
                  totalSessionsForDay > (resistance?.daily_limit || 0) ? 'text-red-600' : 'text-green-600'
                }`}>
                  {totalSessionsForDay}/{resistance?.daily_limit || 0}
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className={`h-2 rounded-full ${
                    totalSessionsForDay > (resistance?.daily_limit || 0)
                      ? 'bg-red-500'
                      : totalSessionsForDay === 0
                      ? 'bg-gray-300'
                      : 'bg-green-500'
                  }`}
                  style={{
                    width: `${Math.min((totalSessionsForDay / (resistance?.daily_limit || 1)) * 100, 100)}%`
                  }}
                />
              </div>
            </div>
          )}

          {/* Session Details */}
          <div>
            <h4 className="font-medium mb-4">Session Details</h4>
            {sessionsForDay.length > 0 ? (
              <div className="space-y-3">
                {sessionsForDay.map((session, index) => (
                  <div key={session.id} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-medium">Session {index + 1}</span>
                      <span className="text-sm text-gray-500">
                        {new Date(session.startedAt).toLocaleTimeString('en-US', {
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </span>
                    </div>
                    <div className="text-sm text-gray-600 space-y-1">
                      {session.reason && (
                        <div className="mt-2">
                          <span className="font-medium">Reason: </span>
                          <span className="italic">"{session.reason}"</span>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                {totalSessionsForDay > 0 ? (
                  <p>Session details not available for this date.</p>
                ) : isFutureDate ? (
                  <div>
                    <div className="text-6xl mb-2">📅</div>
                    <p className="font-medium">This day hasn't occurred yet</p>
                    <p className="text-sm">Come back after {new Date(selectedDate + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} to see your activity</p>
                  </div>
                ) : (
                  <div>
                    <div className="text-6xl mb-2">🎉</div>
                    <p className="font-medium">No sessions on this day!</p>
                    <p className="text-sm">Great self-control!</p>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Close Button */}
          <div className="flex justify-end mt-6">
            <button
              onClick={() => setSelectedDate(null)}
              className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    )
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    )
  }

  if (!resistance) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Resistance not found</h2>
          <button
            onClick={() => navigate('/dashboard')}
            className="text-blue-600 hover:text-blue-800"
          >
            Return to Dashboard
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => navigate('/dashboard')}
              className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">{resistance.domain}</h1>
              <p className="text-gray-600 mt-1">
                {resistance.is_active ? 'Active resistance' : 'Inactive resistance'}
              </p>
            </div>
          </div>

          <div className="flex items-center space-x-3">
            <button
              onClick={() => setIsEditing(!isEditing)}
              className="flex items-center space-x-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              <Edit2 className="w-4 h-4" />
              <span>{isEditing ? 'Cancel' : 'Edit'}</span>
            </button>
            <button
              onClick={handleDelete}
              className="flex items-center space-x-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
            >
              <Trash2 className="w-4 h-4" />
              <span>Delete</span>
            </button>
          </div>
        </div>

        {/* Settings Section - shown when editing */}
        {isEditing && (
          <div className="bg-white rounded-lg border p-6 mb-8">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900">Settings</h2>
              <div className="flex space-x-3">
                <button
                  onClick={handleSave}
                  className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  <Save className="w-4 h-4" />
                  <span>Save</span>
                </button>
                <button
                  onClick={handleCancel}
                  className="flex items-center space-x-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  <X className="w-4 h-4" />
                  <span>Cancel</span>
                </button>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Daily Limit
                </label>
                <input
                  type="number"
                  value={editValues.daily_limit}
                  onChange={(e) => setEditValues(prev => ({ ...prev, daily_limit: parseInt(e.target.value) }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                  min="1"
                />
                <p className="text-xs text-gray-500 mt-1">Maximum number of opens per day</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Session Duration (minutes)
                </label>
                <input
                  type="number"
                  value={editValues.session_duration_minutes}
                  onChange={(e) => setEditValues(prev => ({ ...prev, session_duration_minutes: parseInt(e.target.value) }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                  min="1"
                />
                <p className="text-xs text-gray-500 mt-1">Duration of each allowed session</p>
              </div>
            </div>

            <div className="mt-4">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={resistance.is_active}
                  onChange={(e) => handleUpdate({ is_active: e.target.checked })}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="ml-2 text-sm text-gray-700">Active</span>
              </label>
            </div>
          </div>
        )}

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Calendar Section */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-lg border p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-lg font-semibold text-gray-900 flex items-center space-x-2">
                  <Calendar className="w-5 h-5" />
                  <span>Usage Calendar</span>
                </h2>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => setViewMode('week')}
                    className={`px-3 py-1 text-sm rounded-lg ${
                      viewMode === 'week' ? 'bg-blue-100 text-blue-700' : 'text-gray-600 hover:bg-gray-100'
                    }`}
                  >
                    Week
                  </button>
                  <button
                    onClick={() => setViewMode('month')}
                    className={`px-3 py-1 text-sm rounded-lg ${
                      viewMode === 'month' ? 'bg-blue-100 text-blue-700' : 'text-gray-600 hover:bg-gray-100'
                    }`}
                  >
                    Month
                  </button>
                </div>
              </div>

              {/* Calendar View */}
              {renderCalendar()}
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Summary Stats */}
            <div className="bg-white rounded-lg border p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center space-x-2">
                <BarChart3 className="w-5 h-5" />
                <span>Summary</span>
              </h3>

              <div className="space-y-4">
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Average sessions per day:</span>
                  <span className="font-medium">{getCurrentViewSessions().average.toFixed(1)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Total sessions:</span>
                  <span className="font-medium">{getCurrentViewSessions().total}</span>
                </div>
              </div>
            </div>

            {/* AI Feedback */}
            <div className="bg-white rounded-lg border p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">AI Insights</h3>
              <div className="text-sm text-gray-600 leading-relaxed space-y-3">
                {generateAIInsights()}
              </div>
            </div>
          </div>
        </div>

        {/* Day Detail Modal */}
        {selectedDate && renderDayDetailModal()}
      </div>
    </div>
  )
}